import {ReactNode, useCallback} from 'react';
import {view} from '@sail/ui/deprecated';
import {
  Conversation,
  InlineDataResult as ConversationsClientInlineDataResult,
  InlineDataResultAction,
  InlineDataConversationMessage,
} from '@stripe-internal/conversations-client';
import {
  InlineDataProps,
  InlineDataResult,
  InlineDataServiceContext,
} from '@stripe-internal/inline-data';
import {InlineDataRow} from '../../../Chat/Row/InlineDataRow';
import {InlineDataStripeAssistantMessageRow} from './InlineDataStripeAssistantMessageRow';

type Props = {
  conversation: Conversation;
  message: InlineDataConversationMessage;
  children?: ReactNode;
  avatar?: ReactNode | null;
};

export const InlineDataMessageRow = ({
  conversation,
  message,
  avatar,
  children,
}: Props) => {
  const inlineData = message.inlineData as InlineDataProps;

  const onDone = useCallback(
    (result: InlineDataResult) => {
      const action: InlineDataResultAction = {
        name: 'inline-data-result',
        message,
        result: result as ConversationsClientInlineDataResult,
      };
      return conversation.action(action);
    },
    [conversation, message],
  );

  // until we have a sanctioned path for stripe assistant messages,
  // we're overloading inline data messages. we shortcut here and
  // return an appropriate stripe assistant row
  if (inlineData.name === 'stripe_assistant') {
    return (
      <InlineDataStripeAssistantMessageRow
        conversation={conversation}
        message={message}
        avatar={avatar}
      >
        {children}
      </InlineDataStripeAssistantMessageRow>
    );
  }

  const background =
    message.participant.id === conversation.user.id
      ? 'neutral600'
      : 'neutral100';

  const position =
    message.participant.id === conversation.user.id ? 'end' : 'start';

  const {style} = message.inlineData;
  const serviceContext: InlineDataServiceContext = {
    conversation_id: conversation.id,
    effect_id: message.effectId,
  };

  return (
    <view.div
      data-testid="inline-data-message-row"
      className="uh-conversations-inline-data-message-row"
      data-message-type={message.participant.role}
    >
      <InlineDataRow
        inlineData={{...inlineData, context: serviceContext, onDone}}
        background={background}
        position={position}
        avatar={avatar}
        style={style}
      >
        {children}
      </InlineDataRow>
    </view.div>
  );
};
